/**
 PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA

 *  Copyright © 2017 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * A class for mathml utilities.
 *
 */
import ReactDOM from 'react-dom';

export default class MathUtil {
  /**
   * Convert the string content to HTML element
   *
   * @param htmlContent - String HTML content to be escaped
   * @returns html content.
   */
  static unEscapeHtml = (htmlContent) => {
    let content = htmlContent;
    if (typeof (htmlContent) === 'string') {
      content = htmlContent.replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'");
    }
    return content;
  };

  /**
   * Find the leaf HTML DOM and unescape the string to html
   *
   * @param elementDOM - DOM element to iterate
   */
  static replaceMathML = (elementDOM) => {
    if (elementDOM) {
      if (elementDOM.children !== null && elementDOM.children !== undefined) {
        if (elementDOM.children.length === 0) {
          elementDOM.innerHTML = MathUtil.unEscapeHtml(elementDOM.innerHTML); //eslint-disable-line
        } else {
          for (let e = 0; e < elementDOM.children.length; e += 1) {
            MathUtil.replaceMathML(elementDOM.children[e]);
          }
        }
      } else {
        elementDOM.innerHTML = MathUtil.unEscapeHtml(elementDOM.innerHTML); //eslint-disable-line
      }
    }
  }

  /**
   * Renders Mathml in react component call this method in component ref
   *
   * @param element - React element reference
   */
  static renderMath = (element, mathJaxVersion) => {
    const elementDOM = ReactDOM.findDOMNode(element); //eslint-disable-line
    MathUtil.processMath(null, mathJaxVersion);
  }

  /**
   * Process the MathML content using MathJax
   *
   * @param elementDOM - DOM element containing MathML content
   */
  static processMath = (elementDOM, mathJaxVersion) => {
    // Check is MathJax script loaded or include it
    if (typeof window.MathJax !== 'undefined' && typeof window.MathJax.Hub !== 'undefined') {
      // Do MathJax processing for specific DOM element or entire HTML content
      if (elementDOM) {
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, elementDOM]);
      } else {
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
      if (window.MathJax.Hub.config && window.MathJax.Hub.config.menuSettings
        && window.MathJax.Hub.config.menuSettings.inTabOrder) {
        // Set to false if math elements should not be included in the tabindex
        window.MathJax.Hub.config.menuSettings.inTabOrder = false;
      }
    } else if (typeof window.MathJax !== 'undefined' && window.MathJax.version === '3.2.2') {
      if (elementDOM) {
        window.MathJax.typeset([elementDOM]);
      } else {
        window.MathJax.typeset();
      }
    } else {
      MathUtil.loadMathJax(mathJaxVersion);
    }
  }

  /**
   * Setting the pause between input and output phases of MathJax’s processing.
   *
   * @param delay - Delay in milliseconds
   */
  static setDelay = (delay) => {
    // Check is MathJax script loaded or include it
    if (typeof window.MathJax !== 'undefined' && typeof window.MathJax.Hub !== 'undefined') {
      window.MathJax.Hub.processSectionDelay = delay;
    }
  }

  /**
   * Rerender the MathML content using MathJax
   *
   * @param elementDOM - DOM element containing MathML content
   */
  static rerenderMath = (elementDOM, mathJaxVersion) => {
    // Check is MathJax script loaded or include it
    if (typeof window.MathJax !== 'undefined' && typeof window.MathJax.Hub !== 'undefined') {
      // Do MathJax processing for specific DOM element or entire HTML content
      if (elementDOM) {
        window.MathJax.Hub.Queue(
          [MathUtil.setDelay, 0],
          ['Rerender', window.MathJax.Hub, elementDOM],
          [MathUtil.setDelay, 50]
        );
      } else {
        window.MathJax.Hub.Queue(
          [MathUtil.setDelay, 0],
          ['Rerender', window.MathJax.Hub],
          [MathUtil.setDelay, 50]
        );
      }
    } else {
      MathUtil.loadMathJax(mathJaxVersion);
    }
  }

  /**
   * Dynamicly includes the MathJax script
   *
   */
  static loadMathJax = (mathJaxVersion) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = mathJaxVersion === '3.2.2' ? 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/mml-chtml.min.js' : 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.0/MathJax.js?config=TeX-AMS-MML_HTMLorMML';
    document.getElementsByTagName('head')[0].appendChild(script);
    script.onload = () => {
      // Set to false if math elements should not be included in the tabindex
      if (window.MathJax.version !== '3.2.2') {
        window.MathJax.Hub.config.menuSettings.inTabOrder = false;
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/util/MathUtil.jsx